import { useMutation } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useApiClient } from '@/api/ApiClientProvider'
import type {
  AdAttributionReconciliation,
  AdReconciliationQuery,
  DiffNullReason,
  ReconciliationFigures,
  ReconciliationLineKind,
  ReconciliationMetaOnly,
} from '@/api/advertisingOverview'
import { ADVERTISING_LEVEL_LABELS, ADVERTISING_LEVELS, ATTRIBUTION_MODEL_LABELS, type AttributionModel } from '@/api/types'
import { TmtAttributedBadge } from '@/components/TmtAttributedBadge'
import { Card, DataTable, EmptyState, formatInteger, SegmentedControl, type Column } from '@/design'
import { ProviderAttributedBadge } from '@/features/analytics/advertising/AdvertisingBadges'
import { advertisingErrorMessage } from '@/features/analytics/advertising/errors'
import { formatMoney, windowLabel } from '@/features/analytics/advertising/metrics'
import { CardQueryBody } from '@/features/overview/CardQueryBody'
import { usePermissions } from '@/lib/access'
import { saveBlob } from '@/lib/download'
import { resolvePeriod, type GlobalFilters } from '@/lib/filters'

import {
  DIFF_NULL_TEXT,
  formatSignedCount,
  formatSignedMoney,
  formatSignedPct,
  LINE_DESCRIPTIONS,
  LINE_LABELS,
  META_NOT_AVAILABLE_TEXT,
  META_ONLY_LABEL,
  windowsLabel,
} from './definitions'
import { advertisingSectionError, isAdvertisingFlagOff } from './errors'
import { FlagOffNote } from './SectionStates'
import { useAdReconciliation, useReconciliationLevel } from './overviewQuery'
import { Reasoned } from './Reasoned'

type PanelRowKind = 'object' | ReconciliationLineKind | 'objects_total' | 'total'

/** One table row: an object, a TmT-only line, or a total — each with both sides and the gap. */
interface PanelRow {
  key: string
  kind: PanelRowKind
  label: string
  description: string | null
  figures: ReconciliationFigures
}

const LEVEL_OPTIONS = ADVERTISING_LEVELS.map((level) => ({ value: level, label: ADVERTISING_LEVEL_LABELS[level] }))

/** The download's name, as the API's `Content-Disposition` gives it. */
export function reconciliationCsvFilename(query: AdReconciliationQuery): string {
  return `ad-attribution-reconciliation-${query.level}-${query.from}-to-${query.to}-${query.model}.csv`
}

function panelRows(report: AdAttributionReconciliation): PanelRow[] {
  const level = ADVERTISING_LEVEL_LABELS[report.level].toLowerCase()
  return [
    ...report.rows.map((row) => ({
      key: `object:${row.id}`,
      kind: 'object' as const,
      label: row.name ?? `Not in the imported hierarchy (${row.id})`,
      description: row.deliveryInRange ? null : `No Meta delivery in the range; listed for its TmT-attributed purchases.`,
      figures: row,
    })),
    ...report.lines.map((line) => ({
      key: `line:${line.kind}`,
      kind: line.kind,
      label: LINE_LABELS[line.kind],
      description: LINE_DESCRIPTIONS[line.kind],
      figures: line,
    })),
    {
      key: 'objects_total',
      kind: 'objects_total' as const,
      label: `Total of the ${level} rows`,
      description: 'The rows above the TmT-only lines.',
      figures: report.objectsTotal,
    },
    {
      key: 'total',
      kind: 'total' as const,
      label: 'Total',
      description: 'Every row and both TmT-only lines: every TmT-attributed purchase here against every Meta-reported one.',
      figures: report.total,
    },
  ]
}

function diffReason(reason: DiffNullReason | null) {
  const text = DIFF_NULL_TEXT[reason ?? 'meta_not_available']
  return <Reasoned label={text.label} why={text.why} />
}

function metaCell(figures: ReconciliationFigures, value: number | null, format: (value: number) => string) {
  if (value !== null) return format(value)
  const text = META_NOT_AVAILABLE_TEXT[figures.meta.notAvailable ?? 'not_imported']
  return <Reasoned label={text.label} why={text.why} />
}

function columns(currency: string): Column<PanelRow>[] {
  const money = (minor: number) => formatMoney(minor, currency)
  return [
    {
      key: 'label',
      header: 'Meta object',
      cell: (row) => {
        const Strong = row.kind === 'total' || row.kind === 'objects_total' ? 'strong' : 'span'
        return (
          <span className="aov-recon__label" data-row-kind={row.kind}>
            <Strong>{row.label}</Strong>
            {row.description ? <span className="aov-recon__description">{row.description}</span> : null}
          </span>
        )
      },
    },
    {
      key: 'metaPurchases',
      header: 'Purchases · Meta-reported',
      align: 'end',
      cell: (row) => metaCell(row.figures, row.figures.meta.purchases, formatInteger),
    },
    { key: 'tmtPurchases', header: 'Purchases · TmT', align: 'end', cell: (row) => formatInteger(row.figures.tmt.purchases) },
    {
      key: 'diffPurchases',
      header: 'Purchases difference',
      align: 'end',
      cell: (row) =>
        row.figures.diff.purchases === null ? diffReason(row.figures.diff.purchasesNullReason) : formatSignedCount(row.figures.diff.purchases),
    },
    {
      key: 'diffPurchasesPct',
      header: 'Purchases difference %',
      align: 'end',
      cell: (row) =>
        row.figures.diff.purchasesPct === null
          ? diffReason(row.figures.diff.purchasesPctNullReason)
          : formatSignedPct(row.figures.diff.purchasesPct),
    },
    {
      key: 'metaRevenue',
      header: 'Revenue · Meta-reported',
      align: 'end',
      cell: (row) => metaCell(row.figures, row.figures.meta.revenueMinor, money),
    },
    { key: 'tmtRevenue', header: 'Revenue · TmT', align: 'end', cell: (row) => money(row.figures.tmt.revenueMinor) },
    {
      key: 'diffRevenue',
      header: 'Revenue difference',
      align: 'end',
      cell: (row) =>
        row.figures.diff.revenueMinor === null
          ? diffReason(row.figures.diff.revenueNullReason)
          : formatSignedMoney(row.figures.diff.revenueMinor, currency),
    },
    {
      key: 'diffRevenuePct',
      header: 'Revenue difference %',
      align: 'end',
      cell: (row) =>
        row.figures.diff.revenuePct === null ? diffReason(row.figures.diff.revenuePctNullReason) : formatSignedPct(row.figures.diff.revenuePct),
    },
  ]
}

/**
 * What Meta reports beyond what TmT traced, on the totals only: still Meta's claim, beside
 * the table and never in a TmT column. Nothing when the API gives neither figure.
 */
function MetaOnlyNote({
  metaOnly,
  currency,
  attributionWindows,
}: {
  metaOnly: ReconciliationMetaOnly
  currency: string
  attributionWindows: readonly string[]
}) {
  const parts = [
    metaOnly.purchases === null ? null : `${formatInteger(metaOnly.purchases)} ${metaOnly.purchases === 1 ? 'purchase' : 'purchases'}`,
    metaOnly.revenueMinor === null ? null : formatMoney(metaOnly.revenueMinor, currency),
  ].filter((part): part is string => part !== null)
  if (parts.length === 0) return null
  return (
    <section className="aov-recon__meta-only" aria-labelledby="aov-recon-meta-only">
      <p>
        <ProviderAttributedBadge attributionWindows={attributionWindows} />{' '}
        <strong id="aov-recon-meta-only">{META_ONLY_LABEL}:</strong> <span data-testid="meta-only-figures">{parts.join(' · ')}</span>
      </p>
      <p className="aov-note">
        In the whole range, Meta reports this much more than TmT traced. It is Meta’s count — not in any TmT figure and not
        lost sales — and TmT has no visit to check it against. The reasons are below, view-through first.
      </p>
    </section>
  )
}

function ReconciliationCsvButton({ query }: { query: AdReconciliationQuery }) {
  const client = useApiClient()
  const download = useMutation({
    mutationFn: () => client.adAttributionReconciliationCsv(query),
    onSuccess: (blob) => saveBlob(blob, reconciliationCsvFilename(query)),
  })
  return (
    <>
      <button type="button" className="ci-button" disabled={download.isPending} onClick={() => download.mutate()}>
        {download.isPending ? 'Preparing CSV…' : 'Download CSV'}
      </button>
      {download.isError ? (
        <p role="alert" className="adv-error">
          {advertisingErrorMessage(download.error, 'The CSV could not be prepared just now. Try again in a moment.')}
        </p>
      ) : null}
    </>
  )
}

function ReconciliationBody({ report }: { report: AdAttributionReconciliation }) {
  const rows = useMemo(() => panelRows(report), [report])
  return (
    <div className="aov-recon">
      <div className="aov-recon__sides">
        <p>
          <ProviderAttributedBadge attributionWindows={report.attributionWindows} />{' '}
          <span>Meta’s own count, under its attribution windows ({windowsLabel(report.attributionWindows)}).</span>
        </p>
        <p>
          <TmtAttributedBadge model={report.model} lookbackDays={report.tmtLookbackDays} className="adv-tmt-badge" allWebsites />{' '}
          <span>
            Orders TmT credited to a visit from the ad under the {ATTRIBUTION_MODEL_LABELS[report.model].toLowerCase()} model,
            looking back up to {report.tmtLookbackDays} days.
          </span>
        </p>
        <p className="aov-note">
          A difference is TmT − Meta: negative when Meta reported more. Its percentage is of Meta’s figure. Both sides are
          shown in full; the gap is never netted or hidden.
        </p>
      </div>
      <DataTable caption="Meta-reported vs TmT-attributed purchases and revenue" rows={rows} rowKey={(row) => row.key} columns={columns(report.currency)} />
      <MetaOnlyNote metaOnly={report.total.metaOnly} currency={report.currency} attributionWindows={report.attributionWindows} />
      <section className="aov-recon__causes" aria-labelledby="aov-recon-causes">
        <h3 id="aov-recon-causes">Why the two differ</h3>
        <ul>
          {report.causes.map((cause) => (
            <li key={cause.code} data-cause={cause.code}>
              {cause.text}
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}

/**
 * Meta-reported against TmT-attributed purchases and revenue, per campaign, ad set or
 * ad, the TmT-only lines and the totals, with the signed gap and its percentage of
 * Meta's figure, and the API's reasons the two differ, verbatim. Tenant-wide and
 * production only — the route takes no website or environment. Downloadable as CSV
 * with `exports.create`.
 */
export function ReconciliationPanel({ filters, model }: { filters: GlobalFilters; model: AttributionModel }) {
  const { can } = usePermissions()
  const [level, setLevel] = useReconciliationLevel()
  const { current } = resolvePeriod(filters, new Date())
  const query: AdReconciliationQuery = { from: current.from, to: current.to, level, model }
  const report = useAdReconciliation(query)

  return (
    <Card
      title="Meta-reported vs TmT-attributed"
      subtitle={`${windowLabel(query.from, query.to)} · every website’s production data — the website and environment filters do not apply`}
      actions={
        <div className="aov-recon__actions">
          <SegmentedControl label="Reconciliation level" options={LEVEL_OPTIONS} value={level} onChange={setLevel} />
          {can('exports.create') && report.data ? <ReconciliationCsvButton query={query} /> : null}
        </div>
      }
      busy={report.isFetching}
      className="aov-recon-card"
    >
      {isAdvertisingFlagOff(report.error) ? (
        <FlagOffNote />
      ) : (
        <CardQueryBody
          query={report}
          loadingRows={6}
          loadingLabel="Loading the reconciliation"
          errorDescription={advertisingSectionError(report.error, 'The reconciliation could not be loaded just now. Try again in a moment.')}
        >
          {(data) =>
            data.rows.length === 0 && data.total.tmt.purchases === 0 ? (
              <EmptyState
                variant="no-results"
                compact
                title="Nothing to reconcile in this period"
                description="Meta reported no delivery and TmT attributed no purchase to an ad in this range."
              />
            ) : (
              <ReconciliationBody report={data} />
            )
          }
        </CardQueryBody>
      )}
    </Card>
  )
}
